import React, { useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Copy, Plus, Trash2, Check } from "lucide-react";

export default function SchemaGen() {
  const { current, projects, selectProject } = useProject();
  const [items, setItems] = useState([
    { question: "What is our product?", answer: "A brief definition of what you do." },
  ]);
  const [scriptTag, setScriptTag] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (i, k, v) => {
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  };
  const add = () => setItems((p) => [...p, { question: "", answer: "" }]);
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/schema/faq", {
        items: items.filter((x) => x.question.trim() && x.answer.trim()),
      });
      setScriptTag(r.data.script_tag);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(scriptTag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Technical</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          FAQ Schema Generator
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Feed in your Q&amp;As. Copy-paste the JSON-LD block into your page's
          <span className="mono"> &lt;head&gt; </span>. Google, ChatGPT, and
          Perplexity read this to build their answer capsules.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <section className="card-flat p-5 lg:col-span-7 space-y-4" data-testid="faq-inputs">
          {items.map((it, i) => (
            <div key={i} className="border border-zinc-200 rounded-[4px] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">Q&amp;A {String(i + 1).padStart(2, "0")}</span>
                {items.length > 1 && (
                  <button
                    onClick={() => remove(i)}
                    data-testid={`faq-remove-${i}`}
                    className="text-zinc-400 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <input
                className="w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Question"
                value={it.question}
                onChange={(e) => update(i, "question", e.target.value)}
                data-testid={`faq-q-${i}`}
              />
              <textarea
                className="w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Answer (40-60 words recommended)"
                rows={3}
                value={it.answer}
                onChange={(e) => update(i, "answer", e.target.value)}
                data-testid={`faq-a-${i}`}
              />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <button
              onClick={add}
              data-testid="faq-add"
              className="inline-flex items-center gap-1.5 text-xs border border-zinc-900 px-3 py-1.5 rounded-[4px] hover:bg-zinc-100"
            >
              <Plus size={12} /> Add Q&amp;A
            </button>
            <button
              onClick={generate}
              disabled={loading}
              data-testid="faq-generate"
              className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-4 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Generating…" : "Generate JSON-LD"}
            </button>
          </div>
        </section>

        <section className="card-flat p-5 lg:col-span-5" data-testid="faq-output">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Output</div>
            {scriptTag && (
              <button
                onClick={copy}
                data-testid="faq-copy"
                className="inline-flex items-center gap-1 text-xs border border-zinc-900 px-2.5 py-1 rounded-[4px] hover:bg-zinc-100"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            )}
          </div>
          <pre className="code-block mt-3 text-[11px] whitespace-pre-wrap break-words min-h-[280px]">
            {scriptTag || "// Click 'Generate' to produce a ready-to-paste JSON-LD block."}
          </pre>
        </section>
      </div>
    </DashboardShell>
  );
}
